'use client';

import { websiteEnv } from '@/constants/website-env';
import { useAppContext } from '@/context/app';
import { useIsMobileSsr } from '@/hooks/use-is-mobile-ssr';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Suspense, useRef, useState } from 'react';
import HoverPrefetchLink from '../hover-prefetch-link';
import SearchBarLazyLoad from '../SearchBar/search-bar-lazy-load';
import CartTrigger from './cartTrigger';
import HeaderRectCalculate from './header-rect-calculate';
import RecordInfluencer from './record-influencer';

const CountrySwitcher = dynamic(() => import('./country-switcher'), {
  ssr: false,
  loading: () => <div className='h-5 w-[22px] lg_w-[46px]' />,
});

const HeaderToolbar = props => {
  const {
    miniCartOpen,
    setMiniCartOpen,
    setShowNoItemDrawer,
    announcements,
    headerId,
    navTrigger,
    getShouldResetHeaderOnTop,
    setShouldResetHeaderOnTop,
  } = props;

  const { isDesktop } = useIsMobileSsr();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const headerToolbarRef = useRef<HTMLDivElement>(null);
  const mobileHeaderMenuRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const isWhoIsYourCharacterGame = pathname === '/who-is-your-character';

  const [{ mobileFilterBarFixedOnTop, activeColumn, cartActiveColumn }] = useAppContext();
  const realActiveColumn = pathname === '/cart' ? cartActiveColumn : activeColumn;
  const activeColumnIsHome = pathname === '/attire' ? false : pathname === '/' || realActiveColumn === 'home';
  const activeColumnIsAttire = pathname === '/' ? false : pathname === '/attire' || realActiveColumn === 'attire';

  return (
    <div
      ref={headerToolbarRef}
      className={clsx(mobileFilterBarFixedOnTop && 'invisible')}
    >
      <Suspense fallback={null}>
        <HeaderRectCalculate
          headerToolbarRef={headerToolbarRef}
          mobileHeaderMenuRef={mobileHeaderMenuRef}
          headerId={headerId}
          getShouldResetHeaderOnTop={getShouldResetHeaderOnTop}
          setShouldResetHeaderOnTop={setShouldResetHeaderOnTop}
        />
        <RecordInfluencer />
      </Suspense>
      {announcements}
      <div className='relative flex h-[60px] items-center justify-between lg_h-[100px] lg_pl-5 lg_pr-10'>
        <div className='hidden lg_flex lg_items-center lg_gap-x-10'>
          <Link
            href='/free-wedding-guide'
            className='group flex items-center gap-x-1.5'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='25'
              height='17'
              fill='none'
              viewBox='0 0 25 17'
            >
              <path
                className='stroke-#403732 lg_group-hover_stroke-80583F'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='1.1'
                d='M2.391 2.254H.55V15.99h10.239l1.26-.64 1.262.64H23.55V2.254H21.6'
              ></path>
              <path
                className='stroke-#403732 lg_group-hover_stroke-80583F'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='1.1'
                d='M11.972 15.37c-.95-.41-2.2-.84-3.704-1.08a15.6 15.6 0 0 0-5.712.154V1.017c1.096-.321 3.527-.865 6.329 0a10.6 10.6 0 0 1 3.087 1.543z'
              ></path>
              <path
                className='stroke-#403732 lg_group-hover_stroke-80583F'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='1.1'
                d='M11.972 15.37c.951-.41 2.2-.84 3.705-1.08a15.6 15.6 0 0 1 5.711.154V1.017c-1.096-.321-3.527-.865-6.329 0a10.6 10.6 0 0 0-3.087 1.543z'
              ></path>
            </svg>
            <span className='text-[15px] leading-[1.4] text-#403732 lg_group-hover_text-80583F'>
              Free Wedding Guide
            </span>
          </Link>
        </div>
        <div className='absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 '>
          <Link
            href='/'
            className='flex items-center'
          >
            <svg
              className='h-[35px] w-auto lg_h-[65px]'
              viewBox='0 0 277 65'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                fill-rule='evenodd'
                clip-rule='evenodd'
                d='M50.5257 46.8529C51.7751 46.7353 53.5764 45.7941 54.4771 44.7647C55.465 43.6471 55.9298 42.2353 54.6514 41.2647C53.6345 40.5 52.0365 41.1471 51.3392 41.8235C50.5257 42.6176 50.0318 44.3529 48.027 44.4706C45.7317 44.6176 44.3081 42.5 43.6689 39.0882C43.6398 38.9412 43.6108 38.7647 43.5817 38.5882C43.2621 36.5588 43.204 34.1176 43.4074 31.4412C43.4655 30.5 43.5817 29.3235 43.727 28.0294C43.8432 27.0588 44.1337 25.1176 44.5114 22.2353C43.3493 22.5588 42.2742 22.9706 41.3154 23.4706C40.9668 23.6765 40.5891 23.9118 40.1533 24.1765L39.2235 24.8235C39.0492 26.2353 38.8749 27.7353 38.7005 29.3235C37.7417 29.4118 30.0423 29.4118 27.4274 29.3235C30.8558 24.0294 35.214 17.3235 41.8384 8.41176C41.3736 10.2941 40.7634 13.6765 40.0661 18.5882C41.4607 17.9412 43.0006 17.4118 44.7439 16.9412L45.2669 16.7941C45.4993 15.0588 45.7608 13.1176 46.0513 11C46.5162 7.85294 46.923 5.32353 47.0973 4.14706C47.3588 2.76471 47.6784 1.35294 48.027 0C45.0344 2.58824 41.3154 2.44118 36.5215 2.79412C27.0788 3.58824 21.4712 8.52941 21.4712 15.0294C21.4712 17.3235 22.43 18.7059 24.0862 18.7059C25.3355 18.7059 26.2071 17.5882 26.2071 16.6471C26.2071 15.3824 25.1902 14.2059 25.1902 12.8235C25.1902 11.1176 26.2943 9.14706 27.9504 7.82353C31.0011 5.38235 35.737 5.23529 42.158 4.64706C29.2869 19.2941 21.6165 36.4706 11.9414 46.2941C9.96568 48.2941 8.1643 49.5294 6.1886 49.5294C4.79399 49.5294 3.05072 48.8529 2.12098 48.8529C0.726362 48.8529 0 49.7941 0 50.9706C0 52.7353 1.45272 53.7059 4.15479 53.7059C10.6339 53.7059 17.0259 45.3235 25.8294 31.7059C29.2869 31.6471 36.8701 31.7059 38.7877 31.7059C38.7877 32.0294 38.7877 32.3235 38.7877 32.6471C38.9039 35.4706 39.5141 38.5294 40.56 40.7059C43.0878 45.9706 46.4 47.2353 50.5257 46.8529Z'
                fill='#BE9983'
              />
              <path
                fill-rule='evenodd'
                clip-rule='evenodd'
                d='M30.5078 32.9706C30.3626 33.7647 30.2754 34.5588 30.2754 35.2647C30.2754 37.5588 31.2923 39.7647 33.0356 39.7647C34.2849 39.7647 35.0113 38.7353 35.0113 37.5588C35.0113 36.3235 33.5876 35.0882 33.3842 33L30.5078 32.9706Z'
                fill='#BE9983'
              />
              <path
                fill-rule='evenodd'
                clip-rule='evenodd'
                d='M32.0469 28.9118H33.703H34.8652C36.1436 26.5 38.2064 24.1471 40.7923 22.7647C44.2788 20.9118 48.2302 20.4412 48.2302 20.4412C48.2302 20.4412 47.3586 23.2353 46.6613 27.0882C45.8187 31.7353 45.1504 36.6176 45.1504 40.0294C45.441 40.8824 45.8477 41.7941 46.2545 42.2941C46.9228 43.0882 47.4748 43.2059 47.8525 43.2059H47.9978C48.4917 43.1765 48.8113 42.9706 49.189 42.5882C49.4796 42.3235 49.8282 41.8529 50.1478 41.4118C50.1478 40.9706 50.1769 40.5588 50.2059 40.1176C50.5546 35.0588 50.9613 30.2941 52.2107 24.9412C53.431 19.7941 55.3195 14.8529 55.3195 14.8529C55.3195 14.8529 53.4019 16.2647 51.2519 16.7941C48.114 17.5588 41.8092 18.7059 36.9861 22.2647C34.8361 23.8824 33.18 26.5 32.0469 28.9118Z'
                fill='#BE9983'
              />
              <path
                fill-rule='evenodd'
                clip-rule='evenodd'
                d='M96.9262 10.4118C95.5606 10.4118 94.4565 11.3824 94.4565 12.7059C94.4565 14.1765 95.9964 15.7941 95.9964 19.1176C95.9964 23.6765 93.1491 29.0588 88.5004 36.1471C82.6604 44.9118 77.4306 50.7353 74.1475 54.9412C74.2056 46.6471 74.2346 37.4706 74.8448 30C75.4549 22.5294 76.6752 16.7353 76.6752 16.7353C76.6752 16.7353 72.288 19.9706 69.0048 24.9118C65.2568 30.5 61.5959 37.4706 58.429 42.9118C54.5066 49.7059 51.3107 54.6176 51.3107 54.6176C51.3107 54.6176 50.7877 50.5882 50.439 48.1765C50.0323 48.1765 49.6255 48.2059 49.2478 48.2059C47.8532 48.2059 46.6038 47.9706 45.4707 47.5294C45.8775 53.7059 46.6038 59.3235 47.3592 64.0588C61.8574 46.2353 66.6805 31.9412 70.8934 26.6176C70.0798 31.3235 69.5859 36.2059 69.4406 41.0588L69.7893 63.1176C75.5421 57.5882 80.3651 51.3824 86.8443 42.7647C95.5025 31.2059 100.151 22.5 100.151 16.4412C100.122 12.6471 98.96 10.4118 96.9262 10.4118Z'
                fill='#BE9983'
              />
              <path
                fill-rule='evenodd'
                clip-rule='evenodd'
                d='M126.846 36.4118C126.817 36.4118 126.817 36.4118 126.787 36.3824C126.497 36.3235 126.177 36.2941 125.858 36.2647C125.306 36.2353 124.637 36.2059 124.027 36.2059C124.289 36.1471 124.521 36.0882 124.754 36.0294C127.049 35.4118 128.415 34.0882 128.415 31.9118C128.415 28.4118 125.364 27.2353 120.25 27.2353H116.531V27.5C117.722 27.5588 117.955 27.9118 117.955 29.2941V45.7647C117.955 47.1176 117.722 47.5 116.531 47.5588V47.7941H124.376C129.402 47.7941 132.569 46.0294 132.569 42C132.54 38.7647 130.477 36.9412 126.846 36.4118ZM126.41 31.9412C126.41 33.5 125.916 34.8235 124.202 35.5588C123.33 35.9412 122.168 36.1765 120.57 36.2353H119.756V27.6176C125.189 27.6176 126.41 29.4412 126.41 31.9412ZM123.882 47.4706H119.785V36.6176H123.853C129.286 36.6176 130.536 39.1765 130.536 42.0588C130.536 44.9412 129.112 47.4706 123.882 47.4706Z'
                fill='#403732'
              />
              <path
                fill-rule='evenodd'
                clip-rule='evenodd'
                d='M163.368 47.7647C161.48 47.4411 161.421 44.3529 160.492 41.7059C159.62 39.2647 157.732 38.3823 155.843 38.0882C159.533 37.6176 161.48 35.8529 161.48 32.7941C161.48 29.2059 158.836 27.3823 153.78 27.3823H147.504V27.647C148.579 27.7059 148.812 28.0882 148.812 29.3823V45.8823C148.812 47.2647 148.579 47.6176 147.33 47.6764V47.9117H152.008V47.7059C150.904 47.647 150.671 47.2353 150.671 45.9411V38.2059H152.502C156.337 38.2059 157.528 38.2059 158.69 42.8235C159.591 46.3235 160.695 47.9706 162.961 48.147C163.252 48.1764 163.542 48.147 163.804 48.0882V47.7647C163.688 47.7941 163.513 47.7941 163.368 47.7647ZM150.7 37.8529V27.7647H153.315C158.255 27.7647 159.504 30.1176 159.504 32.8235C159.504 35.5294 158.255 37.8823 153.315 37.8823H150.7V37.8529Z'
                fill='#403732'
              />
              <path
                fill-rule='evenodd'
                clip-rule='evenodd'
                d='M179.232 27.6764C180.627 27.6764 180.889 28 180.889 29.4706V45.9412C180.889 47.3823 180.627 47.7059 179.232 47.7353V47.9706H184.404V47.7353C183.01 47.7353 182.748 47.3823 182.748 45.9412V29.4706C182.748 28 183.01 27.7059 184.404 27.6764V27.4117H179.232V27.6764Z'
                fill='#403732'
              />
              <path
                fill-rule='evenodd'
                clip-rule='evenodd'
                d='M208.548 27.4117H199.773V27.6764C201.139 27.7059 201.401 28 201.401 29.4706V45.9412C201.401 47.3823 201.139 47.7059 199.773 47.7353V47.9706H208.548C215.782 47.9706 219.792 43.3529 219.792 37.6764C219.792 32 215.782 27.4117 208.548 27.4117ZM208.025 47.6176H203.26V27.7647H208.025C214.94 27.7647 217.758 32.2941 217.758 37.6764C217.758 43.0588 214.94 47.6176 208.025 47.6176Z'
                fill='#403732'
              />
              <path
                fill-rule='evenodd'
                clip-rule='evenodd'
                d='M249.397 45.9412L242.163 28.647L241.64 27.4117H241.291L240.478 29.5588L234.231 45.9412C233.737 47.1764 233.272 47.7353 231.965 47.7353H231.471V47.9706H236.381V47.7353H235.8C234.318 47.7353 233.998 47.4117 234.55 45.9412L236.119 41.8823L236.41 41.1176L236.817 40.0294L236.846 39.9706L237.659 37.8235H243.964L244.952 40.147L245.213 40.7647L245.359 41.1176L245.678 41.8823L247.363 45.9412C247.974 47.3529 248.061 47.7353 246.579 47.7353H245.998V47.9706H252.274V47.7353H251.954C250.472 47.7353 250.037 47.3823 249.397 45.9412ZM237.776 37.5588L240.652 29.9706L241.001 30.7647L242.599 34.5588L243.877 37.5588H237.776Z'
                fill='#403732'
              />
              <path
                fill-rule='evenodd'
                clip-rule='evenodd'
                d='M276.768 45.2941C276.768 46.7941 276.448 47.6176 274.996 47.6176H270.056V29.4706C270.056 28 270.318 27.7059 271.683 27.6764V27.4117H266.512V27.6764C267.906 27.7059 268.168 28 268.168 29.4706V45.9412C268.168 47.3823 267.906 47.7059 266.512 47.7353V47.9706H277V45.0294H276.768V45.2941Z'
                fill='#403732'
              />
            </svg>
          </Link>
        </div>

        {/* 3. 左侧占位（如果左边没有其他按钮，可以放一个空的 div） */}
        <div className='flex-1 lg_flex-none'></div>
        <div className='hidden lg_flex lg_items-center lg_gap-x-10'>
          <HoverPrefetchLink
            href='/swatches'
            className='group flex items-center gap-x-1.5'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='19'
              height='23'
              fill='none'
              viewBox='0 0 19 23'
            >
              <g
                className='fill-#403732 lg_group-hover_fill-80583F'
                clipPath='url(#clip0_12101_8630)'
              >
                <path d='M16.328 22.032H1.878A1.879 1.879 0 010 20.156V5.867h18.207v14.29a1.879 1.879 0 01-1.879 1.875zM1.058 6.919v13.234c0 .454.37.82.82.82h14.447c.454 0 .821-.37.821-.82V6.92H1.057z' />
                <path d='M18.207 6.919H0V1.907C0 .872.842.031 1.878.031h14.447c1.036 0 1.878.841 1.878 1.876V6.92h.004zM1.057 5.863H17.15V1.907c0-.454-.37-.82-.822-.82H1.878a.824.824 0 00-.82.82v3.956z' />
                <path d='M15.114 5.86H3.074v1.055h12.04V5.86z' />
                <path d='M3.125 19.3h-.017c-.18-.003-.444-.07-.688-.366-.518-.626-.12-1.281.141-1.71.166-.271.339-.553.339-.81 0-.27-.088-.384-.247-.584-.2-.254-.476-.598-.476-1.239 0-.64.275-.985.476-1.239.159-.2.247-.31.247-.584 0-.275-.088-.384-.247-.584-.2-.254-.476-.598-.476-1.239 0-.64.275-.985.476-1.239.159-.2.247-.31.247-.58 0-.272-.088-.384-.247-.585-.2-.253-.476-.598-.476-1.239 0-.64.275-.985.476-1.238l.159-.201.257-.004h12.296l.159.201c.2.253.476.598.476 1.239 0 .64-.275.985-.476 1.239-.159.2-.247.31-.247.584 0 .27.088.384.247.584.2.254.476.598.476 1.239 0 .64-.275.985-.476 1.239-.159.2-.247.31-.247.584 0 .271.088.384.247.584.2.254.476.599.476 1.24 0 .608-.29.992-.5 1.27-.205.27-.268.366-.22.58.085.387.255.651.417.908.236.373.599.94.063 1.588-.243.295-.507.362-.687.366h-.018c-.433 0-.73-.331-.99-.623-.116-.13-.363-.405-.462-.405-.098 0-.148.042-.335.282-.211.277-.535.693-1.17.693-.634 0-.958-.419-1.17-.694-.187-.239-.236-.281-.335-.281-.098 0-.148.042-.334.282-.212.277-.536.693-1.17.693-.635 0-.96-.419-1.17-.694-.184-.239-.237-.281-.332-.281-.099 0-.148.042-.335.282-.211.277-.535.693-1.17.693-.638 0-.958-.419-1.173-.694-.187-.239-.236-.281-.335-.281-.099 0-.342.274-.462.405-.257.288-.553.62-.987.62zm7.472-2.083c.638 0 .959.418 1.17.693.183.24.236.282.335.282.099 0 .148-.043.335-.282.211-.278.535-.693 1.17-.693.546 0 .93.4 1.212.714-.003-.007-.01-.014-.014-.021-.183-.285-.43-.68-.557-1.25-.151-.696.173-1.126.409-1.439.187-.25.286-.39.286-.633 0-.271-.089-.384-.247-.585-.201-.253-.476-.598-.476-1.239 0-.64.275-.985.476-1.238.158-.2.247-.31.247-.585 0-.27-.089-.383-.247-.584-.201-.253-.476-.598-.476-1.239 0-.64.275-.985.476-1.238.158-.201.247-.31.247-.585a.666.666 0 00-.1-.38H3.338a.664.664 0 00-.102.387c0 .271.088.384.246.585.201.253.476.598.476 1.238 0 .641-.275.986-.476 1.24-.158.2-.246.309-.246.583 0 .271.088.384.246.585.201.253.476.598.476 1.239 0 .64-.275.985-.476 1.238-.158.201-.246.31-.246.585 0 .274.088.383.246.584.201.253.476.598.476 1.239 0 .552-.275 1-.493 1.358-.025.042-.057.088-.085.141.282-.31.66-.693 1.195-.693.638 0 .958.418 1.173.693.187.24.237.281.335.281.099 0 .148-.042.335-.281.212-.278.536-.693 1.17-.693.635 0 .959.418 1.17.693.184.24.236.281.332.281.098 0 .148-.042.334-.281.215-.282.536-.7 1.174-.7zM15.508 2.25H8.48v1.056h7.028V2.25zM15.506 4.14h-3.514v1.056h3.514V4.141z' />
              </g>
              <defs>
                <clipPath id='clip0_12101_8630'>
                  <path
                    fill='#fff'
                    d='M0 0H18.207V22H0z'
                    transform='translate(0 .031)'
                  />
                </clipPath>
              </defs>
            </svg>
            <span className='text-[15px] leading-[1.4] text-#403732 lg_group-hover_text-80583F'>
              Get 5 Free Swatches
            </span>
          </HoverPrefetchLink>
          {isDesktop ? <CountrySwitcher /> : null}
        </div>
      </div>

      {isWhoIsYourCharacterGame ? null : (
        <div
          className='mx-auto flex h-[66px] items-center justify-between gap-x-5 bg-f8f3ef px-[15px] lg_hidden'
          ref={mobileHeaderMenuRef}
        >
          {navTrigger}
          <button
            type='button'
            className='flex h-10 min-w-0 flex-1 items-center gap-x-2.5 rounded-[30px] border-none bg-white px-2.5 outline-0'
            onClick={() => {
              setIsSearchOpen(true);
            }}
          >
            <div className='min-w-0 flex-1 text-[15px] text-[#929292]'>
              {websiteEnv.isUnitedKingdom ? 'Search by colour or name' : 'Search by color or name'}
            </div>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='19'
              height='18'
              fill='none'
              viewBox='0 0 19 18'
              className='flex-none'
            >
              <path
                fill='#80583F'
                fillRule='evenodd'
                d='M9.173 15.506c2.023 0 3.843-.81 5.124-2.158 1.28-1.28 2.157-3.1 2.157-5.123s-.809-3.843-2.157-5.124C13.016 1.821 11.196.944 9.173.944S5.331 1.753 4.05 3.1C2.769 4.382 1.892 6.202 1.892 8.225s.81 3.842 2.158 5.123c1.28 1.349 3.1 2.158 5.123 2.158m6.135-1.82 3.64 3.64-.674.674-3.64-3.64c-1.483 1.28-3.37 2.09-5.46 2.09-2.293 0-4.315-.944-5.799-2.427S.948 10.449.948 8.225c0-2.292.944-4.315 2.427-5.798S6.948 0 9.173 0c2.292 0 4.315.944 5.798 2.427S17.398 6 17.398 8.225c0 2.09-.81 4.045-2.09 5.46'
                clipRule='evenodd'
              ></path>
            </svg>
          </button>
          <div className='flex flex-none items-center gap-x-[15px]'>
            <CountrySwitcher />
            <CartTrigger
              miniCartOpen={miniCartOpen}
              setMiniCartOpen={setMiniCartOpen}
              setShowNoItemDrawer={setShowNoItemDrawer}
            />
          </div>
        </div>
      )}
      <SearchBarLazyLoad
        isSearchOpen={isSearchOpen}
        setIsSearchOpen={setIsSearchOpen}
      />
    </div>
  );
};

export default HeaderToolbar;
